import { GameSession, cellIndex, forEachNeighbour } from '@p4p2r0/minesweeper-core';
import type { GameState } from '@p4p2r0/minesweeper-core';
import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { useI18n } from '../i18n';
import { useSound } from '../sound';
import { HAPTICS, vibrate } from '../theme';
import type { FaceMood } from './Face';
import { GameConsole } from './GameConsole';

type StepId = 'intro' | 'reveal' | 'numbers' | 'flag' | 'flagExplain' | 'chord' | 'win';

const STEPS: readonly StepId[] = ['intro', 'reveal', 'numbers', 'flag', 'flagExplain', 'chord', 'win'];

/** First revealed number on the board — what "numbers" points at, if one exists yet. */
function findNumberHint(game: GameState): number | null {
  const board = game.board;
  if (!board) return null;
  for (let i = 0; i < game.revealed.length; i++) {
    if (game.revealed[i] === 1 && board.mine[i] === 0 && board.adjacent[i] > 0) return i;
  }
  return null;
}

/**
 * An actual, still-unflagged mine next to a revealed number — what the
 * "flag" step points at. Without this, the step only told the player to
 * flag "a square you think hides a mine" with nothing highlighted, so a
 * wrong guess was just as likely as a right one; the chord step right after
 * assumes the flagged mines near its target number are real, so a wrong
 * guess there made the guided chord explode instead of the safe reveal it's
 * supposed to demonstrate.
 */
function findFlagHint(game: GameState): number | null {
  const board = game.board;
  if (!board) return null;
  const { width, height } = game.config;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = cellIndex(width, x, y);
      if (game.revealed[index] !== 1) continue;
      if (board.adjacent[index] === 0) continue;
      let mine: number | null = null;
      forEachNeighbour(width, height, x, y, (_nx, _ny, n) => {
        if (mine === null && board.mine[n] === 1 && game.revealed[n] === 0 && game.flagged[n] === 0) {
          mine = n;
        }
      });
      if (mine !== null) return mine;
    }
  }
  return null;
}

/** A revealed number whose flagged neighbours already match it, with something left to open. */
function findChordHint(game: GameState): number | null {
  const board = game.board;
  if (!board) return null;
  const { width, height } = game.config;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = cellIndex(width, x, y);
      if (game.revealed[index] !== 1) continue;
      const number = board.adjacent[index];
      if (number === 0) continue;
      let flagged = 0;
      let hasTarget = false;
      forEachNeighbour(width, height, x, y, (_nx, _ny, n) => {
        if (game.flagged[n] === 1) flagged++;
        else if (game.revealed[n] === 0) hasTarget = true;
      });
      if (flagged === number && hasTarget) return index;
    }
  }
  return null;
}

/**
 * A guided walkthrough that runs on a real, full-size board using the exact
 * same console the actual game uses — not a shrunken lookalike parked in a
 * side drawer. Each interactive step waits for the player to actually do the
 * thing being described, on their own board, before moving on.
 *
 * Runs on its own offline session so nothing here can touch a ranked game or
 * the leaderboard, no matter who is signed in.
 */
export function TutorialMode({
  onDone,
  flagMode,
  onFlagModeChange,
}: {
  onDone: () => void;
  /** Same setting as the real game — the tutorial teaches whichever gesture
      scheme the player is actually about to play with, not a fixed one. */
  flagMode: boolean;
  onFlagModeChange: (flagMode: boolean) => void;
}): React.ReactElement {
  const { t } = useI18n();
  const { play: playSound } = useSound();
  const session = useMemo(() => new GameSession({ client: null }), []);
  const [pressing, setPressing] = useState(false);
  const [tick, setTick] = useState(() => Date.now());
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const dealt = useRef(false);
  useEffect(() => {
    if (dealt.current) return;
    dealt.current = true;
    void session.newGame('beginner');
  }, [session]);

  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const { game, phase } = snapshot;

  useEffect(() => {
    if (phase !== 'playing') return;
    const timer = window.setInterval(() => setTick(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [phase]);

  const hasRevealed = game.moves.some((move) => move.kind === 'reveal');
  const hasFlagged = game.moves.some((move) => move.kind === 'flag');
  const hasChorded = game.moves.some((move) => move.kind === 'chord');

  // Advance the instant the described action actually happens — no "Next"
  // required for the steps that are themselves the lesson.
  useEffect(() => {
    if (step === 'reveal' && hasRevealed) setStepIndex(STEPS.indexOf('numbers'));
    else if (step === 'flag' && hasFlagged) setStepIndex(STEPS.indexOf('flagExplain'));
    else if (step === 'chord' && hasChorded) setStepIndex(STEPS.indexOf('win'));
  }, [step, hasRevealed, hasFlagged, hasChorded]);

  const numberHint = step === 'numbers' ? findNumberHint(game) : null;
  const flagHint = step === 'flag' ? findFlagHint(game) : null;
  const chordHint = step === 'chord' ? findChordHint(game) : null;

  const over = game.status === 'won' || game.status === 'lost';
  const elapsedMs = over
    ? game.elapsedMs
    : snapshot.startedAt === null
      ? 0
      : Math.max(0, tick - snapshot.startedAt);
  const mood: FaceMood =
    game.status === 'won' ? 'won' : game.status === 'lost' ? 'lost' : pressing ? 'tense' : 'idle';

  const goNext = (): void => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  const goBack = (): void => setStepIndex((i) => Math.max(0, i - 1));

  const newBoard = (): void => {
    setPressing(false);
    setStepIndex(0);
    void session.newGame('beginner');
  };

  let body: string;
  let waiting = false;
  let hintIndex: number | null = null;

  switch (step) {
    case 'intro':
      body = t(flagMode ? 'tutorial.live.introFlagMode' : 'tutorial.live.intro');
      break;
    case 'reveal':
      body = t(flagMode ? 'tutorial.live.revealFlagMode' : 'tutorial.live.reveal');
      waiting = !hasRevealed;
      break;
    case 'numbers':
      body =
        numberHint !== null && game.board
          ? t('tutorial.live.numbersWithExample', { n: game.board.adjacent[numberHint] })
          : t('tutorial.live.numbersNoExample');
      hintIndex = numberHint;
      break;
    case 'flag':
      body =
        flagHint !== null
          ? t(flagMode ? 'tutorial.live.flagWithHintFlagMode' : 'tutorial.live.flagWithHint')
          : t(flagMode ? 'tutorial.live.flagFlagMode' : 'tutorial.live.flag');
      waiting = !hasFlagged;
      hintIndex = flagHint;
      break;
    case 'flagExplain':
      body = t('tutorial.live.flagExplain');
      break;
    case 'chord':
      body = chordHint !== null ? t('tutorial.live.chordPrompt') : t('tutorial.live.chordExplainOnly');
      waiting = chordHint !== null && !hasChorded;
      hintIndex = chordHint;
      break;
    case 'win':
      body = t('tutorial.live.win');
      break;
    default:
      body = '';
  }

  // A loss mid-lesson isn't a failure — just deal a fresh board and keep going
  // from wherever the player was.
  const handledLoss = useRef(false);
  useEffect(() => {
    if (game.status !== 'lost') {
      handledLoss.current = false;
      return;
    }
    if (handledLoss.current) return;
    handledLoss.current = true;
    const timer = window.setTimeout(() => void session.newGame('beginner'), 1400);
    return () => window.clearTimeout(timer);
  }, [game.status, session]);

  return (
    <div className="tutorial-live">
      <div className="tutorial-live-board">
        <GameConsole
          game={game}
          elapsedMs={elapsedMs}
          mood={mood}
          disabled={over}
          onReveal={(x, y) => void session.reveal(x, y)}
          onFlag={(x, y) => {
            if (session.flag(x, y)) {
              vibrate(HAPTICS.flag);
              playSound('flag');
            }
          }}
          onChord={(x, y) => session.chord(x, y)}
          onPressChange={setPressing}
          onNewBoard={newBoard}
          hintIndex={hintIndex}
          flagMode={flagMode}
          onFlagModeChange={onFlagModeChange}
        />
      </div>

      <div className="tutorial-live-panel">
        <div className="tutorial-progress" aria-hidden="true">
          {STEPS.map((id, index) => (
            <span
              key={id}
              className={index < stepIndex ? 'pip done' : index === stepIndex ? 'pip active' : 'pip'}
            />
          ))}
        </div>

        <div className="tutorial-card">
          <p className="tutorial-live-body">{body}</p>
          {waiting ? <p className="faint small tutorial-waiting">{t('tutorial.live.waiting')}</p> : null}

          <div className="tutorial-live-actions">
            <button type="button" className="btn ghost" onClick={onDone}>
              {t('tutorial.live.exit')}
            </button>
            <div className="tutorial-live-actions-right">
              {stepIndex > 0 ? (
                <button type="button" className="btn ghost" onClick={goBack}>
                  {t('tutorial.back')}
                </button>
              ) : null}
              {waiting ? (
                <button type="button" className="btn ghost" onClick={goNext}>
                  {t('tutorial.live.skip')}
                </button>
              ) : stepIndex === STEPS.length - 1 ? (
                <button type="button" className="btn" onClick={onDone}>
                  {t('tutorial.done')}
                </button>
              ) : (
                <button type="button" className="btn" onClick={goNext}>
                  {t('tutorial.next')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
